import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getUserVisitHistory, updateUserAttendance } from '../../../actions/users';
import { useMemo } from 'react';
import { SEND_TOAST } from '../../../types/toast';
import moment from 'moment';
import IconToolTip from '../../../common/IconToolTip';
import { canUpdateCalender } from '../TeamController';
import { ticket as ticketPath } from '../../../router/roleWisePageRoute';

const validateAttendanceAction = (date, action = null) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    const minDate = new Date(today);
    minDate.setMonth(minDate.getMonth() - 2);
    minDate.setHours(0, 0, 0, 0);

    const maxDate = new Date(today);
    maxDate.setMonth(maxDate.getMonth() + 2);
    maxDate.setHours(23, 59, 59, 999);

    if (checkDate < minDate || checkDate > maxDate) {
        return {
            success: false,
            message: `Date must be between ${minDate.toLocaleDateString()} and ${maxDate.toLocaleDateString()} (2 months before and after today)`,
            allowedActions: []
        };
    }

    const isToday = checkDate.getTime() === today.getTime();
    const isPast = checkDate < today;
    const isFuture = checkDate > today;

    let allowedActions = [];

    if (isFuture) {
        allowedActions = ['leave'];
        if (action && !allowedActions.includes(action)) {
            return {
                success: false,
                message: 'Future dates can only be marked as Leave.',
                allowedActions: ['leave']
            };
        }
    }

    else if (isPast || isToday) {
        allowedActions = ['present', 'leave', 'half_day'];
    }

    if (action) {
        if (allowedActions.includes(action)) {
            return {
                success: true,
                message: `Successfully validated: ${action} is allowed for this date`,
                allowedActions
            };
        }
    } else {
        return {
            success: true,
            message: `Allowed actions for this date: ${allowedActions.join(', ')}`,
            allowedActions
        };
    }

    return {
        success: false,
        message: `Action "${action}" is not allowed for this date`,
        allowedActions
    };
};

export default function UpdateAvailabilityStatus({ user_id, full_name, actions }) {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [attendanceData, setAttendanceData] = useState({});
    const [activePopup, setActivePopup] = useState(null);
    const [multiSelectMode, setMultiSelectMode] = useState(false);
    const [selectedDates, setSelectedDates] = useState(new Set());
    // Oldest date for which visit history has been fetched. Starts at today - 2 months
    // and only grows backward as the user navigates to older months (view-only).
    const [loadedFrom, setLoadedFrom] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 2);
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const dispatch = useDispatch();
    const userVisitHistoryByIds = useSelector(state => state.user.userVisitHistoryByIds);
    const isUserVisitHistoryLoading = useSelector(state => state.loading.isUserVisitHistoryLoading);
    const popupRef = useRef(null);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Earliest month the user is allowed to navigate back to for viewing (12 months back).
    const viewMinDate = new Date(today.getFullYear(), today.getMonth() - 12, 1);
    viewMinDate.setHours(0, 0, 0, 0);
    const hasUpdateCalenderPermission = canUpdateCalender(actions);

    const userVisitHistory = useMemo(() => {
        return userVisitHistoryByIds[user_id] || [];
    }, [userVisitHistoryByIds, user_id]);
    // Visit history accumulated across fetches. Each backward navigation loads only the
    // newly-revealed month and merges it in, so already-loaded months are reused (not refetched).
    const [visitHistoryByDate, setVisitHistoryByDate] = useState({});

    useEffect(() => {
        const from = (() => {
            const d = new Date();
            d.setMonth(d.getMonth() - 2);
            d.setHours(0, 0, 0, 0);
            return d;
        })();
        const to = (() => {
            const d = new Date();
            d.setMonth(d.getMonth() + 2);
            d.setHours(23, 59, 59, 999);
            return d;
        })();
        setLoadedFrom(from);
        dispatch(getUserVisitHistory({ userId: user_id, from, to }));

    }, [user_id, dispatch]);

    // Merge each fetch result into the accumulated maps. We only ever fetch newly-revealed
    // months, so this ADDS new dates while preserving already-loaded ones. `prev` wins on
    // any overlap, so optimistic updates (and cleared dates) are never clobbered by a fetch.
    useEffect(() => {
        if (!Array.isArray(userVisitHistory) || userVisitHistory.length === 0) return;

        const newVisits = {};
        userVisitHistory.forEach((dayData) => {
            if (dayData.date && dayData.visits) {
                newVisits[dayData.date] = dayData.visits || [];
            }
        });
        setVisitHistoryByDate(prev => ({ ...newVisits, ...prev }));

        const newStatuses = {};
        userVisitHistory.forEach((record) => {
            if (record.date && record.working_status) {
                const dateKey = moment(record.date).format('YYYY-MM-DD');

                let status = null;
                if (record.working_status === 'WORKING') {
                    status = 'present';
                } else if (record.working_status === 'LEAVE' || record.working_status === 'LEAVE_APPROVED') {
                    status = 'leave';
                } else if (record.working_status === 'HALF_DAY') {
                    status = 'half_day';
                }

                if (status) {
                    newStatuses[dateKey] = { status, date: dateKey };
                }
            }
        });
        setAttendanceData(prev => ({ ...newStatuses, ...prev }));
    }, [userVisitHistory]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setActivePopup(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getDateRange = () => {
        const start = new Date(today);
        start.setMonth(start.getMonth() - 2);
        const end = new Date(today);
        end.setMonth(end.getMonth() + 2);
        return { start, end };
    };

    const { start: minDate, end: maxDate } = getDateRange();

    const getCalendarDays = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const isDateInRange = (date) => {
        if (!date) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate >= minDate && checkDate <= maxDate;
    };

    // Whether a date can be VIEWED (its history has been fetched). This is wider than
    // isDateInRange: it includes older months the user has navigated back to. Updates
    // remain gated by isDateInRange (±2 months); this only controls viewing.
    const isDateViewable = (date) => {
        if (!date) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate >= loadedFrom && checkDate <= maxDate;
    };

    const isDateToday = (date) => {
        if (!date) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate.getTime() === today.getTime();
    };

    const isDatePast = (date) => {
        if (!date) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate < today;
    };

    const isDateFuture = (date) => {
        if (!date) return false;
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate > today;
    };

    const getAllowedActions = (date) => {
        if (!date || !isDateInRange(date)) return [];
        const validation = validateAttendanceAction(date);
        return validation.allowedActions;
    };

    const handleDayClick = (date, event) => {
        if (!date) return;

        if (multiSelectMode) {
            // Bulk update is restricted to the updatable window (±2 months)
            if (!isDateInRange(date)) return;
            // Toggle checkbox selection
            const dateKey = moment(date).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD');
            setSelectedDates(prev => {
                const newSet = new Set(prev);
                if (newSet.has(dateKey)) {
                    newSet.delete(dateKey);
                } else {
                    newSet.add(dateKey);
                }
                return newSet;
            });
        } else {
            // View popup is allowed for any loaded (viewable) date, including older months
            if (!isDateViewable(date)) return;
            // Original single date popup behavior
            const rect = event.currentTarget.getBoundingClientRect();
            setActivePopup({
                date,
                position: { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX }
            });
        }
    };

    const toggleMultiSelectMode = () => {
        setMultiSelectMode(prev => !prev);
        setSelectedDates(new Set());
        setActivePopup(null);
    };

    const isDateSelected = (date) => {
        if (!date) return false;
        const dateKey = moment(date).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD');
        return selectedDates.has(dateKey);
    };

    const handleBulkUpdate = (status) => {
        if (selectedDates.size === 0) return;

        const datesArray = Array.from(selectedDates);
        const validDates = [];
        const invalidDates = [];

        datesArray.forEach(dateKey => {
            const date = new Date(dateKey + 'T00:00:00.000Z');
            const validation = validateAttendanceAction(date, status);
            if (validation.success) {
                validDates.push(date.toISOString());
            } else {
                invalidDates.push({ date: dateKey, message: validation.message });
            }
        });

        if (invalidDates.length > 0) {
            const errorMessages = invalidDates.map(item =>
                `${new Date(item.date).toLocaleDateString()}: ${item.message}`
            ).join('\n');

            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'danger',
                    message: `Some dates could not be updated:\n${errorMessages}`
                }
            });
        }

        if (validDates.length > 0) {
            // Update local state
            const updates = {};
            validDates.forEach(dateISO => {
                const dateKey = moment(dateISO).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD');
                updates[dateKey] = { status, date: dateKey };
            });

            setAttendanceData(prev => ({
                ...prev,
                ...updates
            }));

            // Dispatch bulk update
            dispatch(updateUserAttendance({
                userId: user_id,
                payload: {
                    dates: validDates,
                    working_status: status === 'present' ? 'WORKING' : status === 'half_day' ? 'HALF_DAY' : 'LEAVE_APPROVED'
                },
                formName: 'updateAvailabilityStatus'
            }));

            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'success',
                    message: `Successfully updated ${validDates.length} date(s) as ${status}`
                }
            });
        }

        setSelectedDates(new Set());
        setMultiSelectMode(false);
    };

    const clearSelection = () => {
        setSelectedDates(new Set());
    };

    const updateStatus = (date, status) => {
        if (!date) return;

        // Allow clearing status (empty string) without validation
        if (status !== '') {
            const validation = validateAttendanceAction(date, status);

            if (!validation.success) {
                dispatch({
                    type: SEND_TOAST,
                    payload: {
                        status: 'danger',
                        message: validation.message
                    }
                });
                return;
            }
        }

        const dateKey = moment(date).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD');

        // Remove the date from attendance data if clearing status
        if (status === '') {
            setAttendanceData(prev => {
                const newData = { ...prev };
                delete newData[dateKey];
                return newData;
            });
        } else {
            setAttendanceData(prev => ({
                ...prev,
                [dateKey]: { status, date: dateKey }
            }));
        }

        dispatch(updateUserAttendance({
            userId: user_id,
            payload: {
                dates: [date.toISOString()],
                working_status: status === '' ? 'EMPTY' : status === 'present' ? 'WORKING' : status === 'half_day' ? 'HALF_DAY' : 'LEAVE_APPROVED'
            },
            formName: 'updateAvailabilityStatus'
        }));

        setActivePopup(null);
    };

    const getDateStatus = (date) => {
        if (!date) return null;
        const dateKey = moment(date).add(5, 'hours').add(30, 'minutes').format('YYYY-MM-DD');
        return attendanceData[dateKey];
    };

    const getVisitsForDate = (date) => {
        if (!date) return [];
        const dateKey = moment(date).format('YYYY-MM-DD');
        return visitHistoryByDate[dateKey] || [];
    };

    const formatTime = (dateString) => {
        if (!dateString) return null;
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const changeMonth = (direction) => {
        const newMonth = new Date(currentMonth);
        newMonth.setMonth(newMonth.getMonth() + direction);

        const newMonthStart = new Date(newMonth.getFullYear(), newMonth.getMonth(), 1);
        newMonthStart.setHours(0, 0, 0, 0);

        // Forward stays capped at +2 months; backward viewing is capped at 12 months.
        if (direction > 0 && newMonth > maxDate) return;
        if (direction < 0 && newMonthStart < viewMinDate) return;

        setCurrentMonth(newMonth);

        // Fetch ONLY the newly-revealed range we don't already have (from the new month's
        // start up to just before what's already loaded). Previously-loaded months stay in
        // the accumulated maps, so we never re-request data we already have.
        if (newMonthStart < loadedFrom) {
            const to = new Date(loadedFrom.getTime() - 1);
            setLoadedFrom(newMonthStart);
            dispatch(getUserVisitHistory({ userId: user_id, from: newMonthStart, to }));
        }
    };

    const formatMonthYear = (date) => {
        return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    const calendarDays = getCalendarDays(currentMonth);
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const getUpcomingLeaves = () => {
        const leaves = Object.values(attendanceData)
            .filter(record => record.status === 'leave')
            .map(record => ({
                ...record,
                dateObj: new Date(record.date + 'T00:00:00.000Z')
            }))
            .sort((a, b) => a.dateObj - b.dateObj);

        const todayIST = new Date(today.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
        todayIST.setHours(0, 0, 0, 0);

        const upcomingLeaves = leaves.filter(leave => leave.dateObj >= todayIST).slice(0, 5);

        return upcomingLeaves;
    };

    const upcomingLeaves = getUpcomingLeaves();

    return (
        <div style={{ backgroundColor: '#d0dfff' }} className="overflow-x-hidden">
            <div className="w-full mx-auto p-2 sm:p-4 relative">
                {isUserVisitHistoryLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-50">
                        <div className="flex flex-col items-center space-y-4">
                            <div className="relative">
                                <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
                                <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                            </div>
                            <div className="text-center">
                                <p className="text-blue-600 font-medium">Loading attendance data...</p>
                                <p className="text-gray-500 text-sm">Please wait while we fetch the attendance data</p>
                            </div>
                        </div>
                    </div>
                ) : null}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3 sm:gap-5">
                    <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 w-full max-w-full overflow-hidden">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                <button
                                    className="text-[22px] w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-gray-700 text-lg font-medium transition-colors flex-shrink-0"
                                    onClick={() => changeMonth(-1)}
                                    disabled={new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1) < viewMinDate}
                                >
                                    ‹
                                </button>
                                <h2 className="w-full text-center text-sm sm:text-base font-semibold text-gray-800 truncate">{formatMonthYear(currentMonth)}</h2>
                                <button
                                    className="text-[22px] w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg text-gray-700 text-lg font-medium transition-colors flex-shrink-0"
                                    onClick={() => changeMonth(1)}
                                    disabled={new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1) > maxDate}
                                >
                                    ›
                                </button>
                            </div>
                            {hasUpdateCalenderPermission ? <button
                                className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 flex-shrink-0 min-w-[10rem] ${multiSelectMode
                                    ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-md'
                                    : 'bg-blue-100 hover:bg-blue-200 text-blue-700'
                                    }`}
                                onClick={toggleMultiSelectMode}
                            >
                                <span>{multiSelectMode ? '✓' : '☐'}</span>
                                <span className="hidden sm:inline">{multiSelectMode ? 'Multi-Select' : 'Select Multiple'}</span>
                                <span className="sm:hidden">{multiSelectMode ? 'Multi' : 'Select'}</span>
                            </button> : null}
                        </div>

                        <div className="grid grid-cols-7 gap-1 w-full">
                            {weekDays.map((day, i) => (
                                <div key={i} className="select-none text-center text-[10px] sm:text-[12px] font-semibold text-gray-500 uppercase tracking-wide py-1 sm:py-1.5">{day}</div>
                            ))}

                            {calendarDays.map((date, index) => {
                                if (!date) {
                                    return <div key={`empty-${index}`} className="aspect-square"></div>;
                                }

                                const dateStatus = getDateStatus(date);
                                const isInRange = isDateInRange(date);
                                const isViewable = isDateViewable(date);
                                const isToday = date.toDateString() === today.toDateString();
                                const isSelected = isDateSelected(date);

                                let dayClasses = "aspect-square flex items-center justify-center rounded-lg text-[10px] sm:text-[11px] font-medium cursor-pointer transition-all border-2 relative min-w-0 ";

                                if (multiSelectMode ? !isInRange : !isViewable) {
                                    dayClasses += "opacity-20 cursor-not-allowed bg-gray-50 text-gray-300 border-transparent ";
                                } else if (multiSelectMode) {
                                    // Multi-select mode styling
                                    if (isSelected) {
                                        dayClasses += "bg-blue-100 border-blue-500 text-blue-700 font-semibold shadow-sm ";
                                    } else if (dateStatus?.status === 'present') {
                                        dayClasses += "bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm border-transparent ";
                                    } else if (dateStatus?.status === 'leave') {
                                        dayClasses += "bg-red-400 hover:bg-red-400 text-white font-semibold shadow-sm border-transparent ";
                                    } else if (dateStatus?.status === 'half_day') {
                                        dayClasses += "bg-amber-400 hover:bg-amber-500 text-white font-semibold shadow-sm border-transparent ";
                                    } else {
                                        dayClasses += "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:border-gray-300 border-transparent ";
                                    }
                                } else {
                                    // Single select mode (original behavior)
                                    if (dateStatus?.status === 'present') {
                                        dayClasses += "bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm border-transparent ";
                                    } else if (dateStatus?.status === 'leave') {
                                        dayClasses += "bg-red-400 hover:bg-red-400 text-white font-semibold shadow-sm border-transparent ";
                                    } else if (dateStatus?.status === 'half_day') {
                                        dayClasses += "bg-amber-400 hover:bg-amber-500 text-white font-semibold shadow-sm border-transparent ";
                                    } else if (dateStatus?.status === 'absent') {
                                        dayClasses += "bg-red-500 hover:bg-red-400 text-white font-semibold shadow-sm border-transparent ";
                                    } else {
                                        dayClasses += "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:border-gray-300 border-transparent ";
                                    }
                                }

                                if (isToday && !dateStatus && !multiSelectMode) {
                                    dayClasses += "border-2 border-indigo-500 bg-indigo-200 text-indigo-700 font-bold shadow-sm ";
                                } else if (isToday && dateStatus && !multiSelectMode) {
                                    dayClasses += "ring-2 ring-indigo-400 ring-offset-1 shadow-md ";
                                }

                                return (
                                    <div
                                        key={date.toISOString()}
                                        className={dayClasses}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isViewable) handleDayClick(date, e);
                                        }}
                                    >
                                        {multiSelectMode && isInRange && (
                                            <div className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1">
                                                <div className={`w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-2 flex items-center justify-center ${isSelected
                                                    ? 'bg-blue-500 border-blue-600'
                                                    : 'bg-white border-gray-300'
                                                    }`}>
                                                    {isSelected && (
                                                        <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <span className={multiSelectMode && isSelected ? 'font-bold' : ''}>{date.getDate()}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 h-fit w-full">
                        <h3 className="text-[12px] font-bold text-gray-800 mb-3 pb-2 border-b-2 border-red-300">Upcoming Leaves</h3>
                        {upcomingLeaves.length > 0 ? (
                            <div className="flex flex-col gap-2.5">
                                {upcomingLeaves.map((leave) => {
                                    return (
                                        <div key={leave.date} className="flex items-center gap-2.5 p-2.5 bg-red-50 rounded-lg border-l-4 border-red-300 hover:bg-red-100 transition-all hover:shadow-sm">
                                            <div className="text-lg flex-shrink-0">📅</div>
                                            <div className="flex-1">
                                                <div className="text-xs font-semibold text-gray-800 mb-0.5">
                                                    {leave.dateObj.toLocaleDateString('en-US', {
                                                        weekday: 'short',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </div>
                                                <div className="text-[10px] text-red-600 uppercase font-semibold tracking-wider">Leave</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <div className="text-4xl mb-2 opacity-50">📭</div>
                                <p className="text-[12px] text-gray-400 font-medium">No upcoming leaves</p>
                            </div>
                        )}

                        <div className="mt-5 pt-4 border-t border-gray-200">
                            <h4 className="text-[12px] font-bold text-gray-800 mb-2.5 uppercase tracking-wide">Color Guide</h4>
                            <div className="flex flex-wrap gap-2.5">
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-emerald-500 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Present</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-amber-400 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Half Day</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-red-400 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Leave</span>
                                </div>
                                <div className="flex items-center gap-2.5">
                                    <span className="w-5 h-5 bg-indigo-200 border-2 border-indigo-500 rounded flex-shrink-0 shadow-sm"></span>
                                    <span className="text-[11px] text-gray-700 font-medium">Today</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {activePopup && (
                    <div
                        ref={popupRef}
                        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl p-2 w-[25rem] max-h-[85vh] overflow-y-auto z-50 animate-in fade-in zoom-in duration-150 border border-gray-200"
                    >
                        <div className="text-md font-semibold text-gray-800 px-3 py-2.5 text-center border-b border-gray-200 mb-1.5 bg-gray-50 rounded-t-lg">
                            {activePopup.date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                weekday: 'short'
                            })}
                        </div>

                        {(() => {
                            const allowedActions = getAllowedActions(activePopup.date);
                            const isToday = isDateToday(activePopup.date);
                            const isPast = isDatePast(activePopup.date);
                            const isFuture = isDateFuture(activePopup.date);
                            const visits = getVisitsForDate(activePopup.date);
                            const showVisits = !isFuture && visits.length > 0;
                            // Updates are only allowed inside the ±2 month window. Older months
                            // (still viewable) are read-only: show visits but no update actions.
                            const canUpdateThisDate = hasUpdateCalenderPermission && isDateInRange(activePopup.date);

                            return (
                                <>
                                    {showVisits && (
                                        <div className="mb-3 px-2">
                                            <div className="flex items-center justify-between mb-2 px-1">
                                                <h4 className="text-[11px] font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
                                                    <span>📍</span>
                                                    Visit History ({visits.length})
                                                </h4>
                                            </div>
                                            <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                                                {visits.map((visit, idx) => (
                                                    <div
                                                        key={visit.ticket_id || idx}
                                                        className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 hover:bg-blue-100 transition-colors"
                                                    >
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1.5">
                                                                    <span onClick={() => window.open(`${window.location.origin}${ticketPath.path}?q_t_n=${visit.ticket_number}&tab=all_tickets`, '_blank')} className="cursor-pointer ext-[10px] font-semibold text-blue-700 bg-blue-200 px-1.5 py-0.5 rounded">
                                                                        #{visit.ticket_number || 'N/A'}
                                                                    </span>
                                                                    {visit.check_out_time ? (
                                                                        <span className="text-[9px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded uppercase">
                                                                            Checked Out
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-[9px] font-semibold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded uppercase">
                                                                            Active
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="space-y-1 flex gap-4">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="text-[10px] text-gray-600 font-medium">Check-in:</span>
                                                                        <span className="text-[10px] text-gray-800 font-semibold">
                                                                            {formatTime(visit.check_in_time)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <span className="text-[10px] text-gray-600 font-medium">Check-out:</span>
                                                                        <span className="text-[10px] text-gray-800 font-semibold">
                                                                            {formatTime(visit.check_out_time)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {showVisits && (
                                        <div className="border-t border-gray-200 my-2"></div>
                                    )}

                                    {!canUpdateThisDate && visits.length === 0 ? <div className="px-2 space-y-1">
                                        <div className="text-[10px] font-semibold text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded text-center">
                                            There are no visits for this date
                                        </div>
                                    </div> : null}

                                    {canUpdateThisDate ? <div className="px-2 space-y-1">
                                        {allowedActions.includes('present') ? (
                                            <button
                                                className="w-full px-3 py-2.5 text-left text-md font-medium text-gray-800 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 rounded-md transition-all flex items-center gap-2 border border-transparent"
                                                onClick={() => updateStatus(activePopup.date, 'present')}
                                            >
                                                <span className="text-md w-4 flex items-center justify-center text-emerald-600">✓</span>
                                                Present
                                            </button>
                                        ) : (
                                            <div className="relative group">
                                                <IconToolTip title={isFuture ? 'Present can only be marked for past dates and today' : 'Present is not allowed for this date'}>
                                                    <button
                                                        className="w-full px-3 py-2.5 text-left text-md font-medium text-gray-400 bg-gray-50 cursor-not-allowed rounded-md flex items-center gap-2 border border-gray-200"
                                                        disabled
                                                    >
                                                        <span className="text-md w-4 flex items-center justify-center">✓</span>
                                                        Present
                                                    </button>
                                                </IconToolTip>
                                            </div>
                                        )}

                                        {allowedActions.includes('half_day') ? (
                                            <button
                                                className="w-full px-3 py-2.5 text-left text-md font-medium text-gray-800 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200 rounded-md transition-all flex items-center gap-2 border border-transparent"
                                                onClick={() => updateStatus(activePopup.date, 'half_day')}
                                            >
                                                <span className="text-md w-4 flex items-center justify-center text-amber-600">⏰</span>
                                                Half Day
                                            </button>
                                        ) : (
                                            <div className="relative group">
                                                <IconToolTip title={isFuture ? 'Half Day can only be marked for past dates and today' : 'Half Day is not allowed for this date'}>
                                                    <button
                                                        className="w-full px-3 py-2.5 text-left text-md font-medium text-gray-400 bg-gray-50 cursor-not-allowed rounded-md flex items-center gap-2 border border-gray-200"
                                                        disabled
                                                    >
                                                        <span className="text-md w-4 flex items-center justify-center">⏰</span>
                                                        Half Day
                                                    </button>
                                                </IconToolTip>
                                            </div>
                                        )}

                                        {allowedActions.includes('leave') ? (
                                            <button
                                                className="w-full px-3 py-2.5 text-left text-md font-medium text-gray-800 hover:bg-red-50 hover:text-red-700 hover:border-red-200 rounded-md transition-all flex items-center gap-2 border border-transparent"
                                                onClick={() => updateStatus(activePopup.date, 'leave')}
                                            >
                                                <span className="text-md w-4 flex items-center justify-center text-red-600">📅</span>
                                                Leave
                                            </button>
                                        ) : (
                                            <div className="relative group">
                                                <IconToolTip title={isPast ? 'Leave can only be marked for today and future dates' : 'Leave is not allowed for this date'}>
                                                    <button
                                                        className="w-full px-3 py-2.5 text-left text-md font-medium text-gray-400 bg-gray-50 cursor-not-allowed rounded-md flex items-center gap-2 border border-gray-200"
                                                        disabled
                                                    >
                                                        <span className="text-md w-4 flex items-center justify-center">📅</span>
                                                        Leave
                                                    </button>
                                                </IconToolTip>
                                            </div>
                                        )}

                                        {/* Clear Status Button - Only show when visits are empty and status exists */}
                                        {visits.length === 0 && getDateStatus(activePopup.date) && (
                                            <>
                                                <div className="border-t border-gray-200 my-2"></div>
                                                <button
                                                    className="w-full px-3 py-2.5 text-left text-md font-medium text-gray-800 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-300 rounded-md transition-all flex items-center gap-2 border border-gray-200"
                                                    onClick={() => updateStatus(activePopup.date, '')}
                                                >
                                                    <span className="text-md w-4 flex items-center justify-center text-gray-600">✕</span>
                                                    Clear Status
                                                </button>
                                            </>
                                        )}
                                    </div> : null}
                                </>
                            );
                        })()}

                    </div>
                )}

                {/* Floating Action Bar for Multi-Select */}
                {multiSelectMode && selectedDates.size > 0 && (
                    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border-2 border-blue-500 z-50 p-3 sm:p-4 animate-in slide-in-from-bottom-5 duration-200">
                        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
                            <div className="text-xs sm:text-sm font-semibold text-gray-700 flex-1 text-center sm:text-left">
                                <span className="text-blue-600 font-bold">{selectedDates.size}</span> date{selectedDates.size !== 1 ? 's' : ''} selected
                            </div>
                            <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
                                <button
                                    onClick={() => handleBulkUpdate('present')}
                                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                                >
                                    <span>✓</span>
                                    <span>Present</span>
                                </button>
                                <button
                                    onClick={() => handleBulkUpdate('half_day')}
                                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                                >
                                    <span>⏰</span>
                                    <span>Half Day</span>
                                </button>
                                <button
                                    onClick={() => handleBulkUpdate('leave')}
                                    className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium bg-red-400 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm"
                                >
                                    <span>📅</span>
                                    <span>Leave</span>
                                </button>
                                <button
                                    onClick={clearSelection}
                                    className="px-2 sm:px-3 py-2 text-xs sm:text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center shadow-sm"
                                    title="Clear selection"
                                >
                                    <span className="text-base">✕</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}